<template>
    <v-card width="100%" class="ma-0 pa-0" outlined>
        <v-overlay v-if="loading">
          <v-progress-circular indeterminate color="primary"/>
        </v-overlay>

        <v-expansion-panels v-model="managementPanelExpanded" flat class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header class="blue-grey lighten-5">
              <div>
                <v-icon left>mdi-cog-outline</v-icon>
                <strong>Management</strong>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <c-pre-release-management></c-pre-release-management>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-data-table
            style="width: 100%;"
            fixed-header
            :items="configs"
            :headers="headers"
            hide-default-footer
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            disable-pagination>
          <template v-slot:item.branch.name="{ item }">
            <v-card-text class="accent--text subtitle-2 ma-0 pa-0">{{ item.branch.name }}</v-card-text>
          </template>

          <template v-slot:item.config.group="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :disabled="!ifCanEditConfigTag()"
                       outlined
                       v-bind="attrs"
                       v-on="on"
                       @click="onPressEditGroup(item.config)"
                       small
                       class="orange--text subtitle-2">
                  {{item.config.group}}
                </v-btn>
              </template>
              <span>{{item.config.group}}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.config.tag="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :disabled="!ifCanEditConfigTag()"
                       outlined
                       v-bind="attrs"
                       v-on="on"
                       @click="onPressEditTag(item.config)"
                       small
                       class="green--text subtitle-2">
                  {{getTagText(item.config.tag)}}
                </v-btn>
              </template>
              <span>{{item.config.tag }}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.config.sharedConfig="{ item }">
            <v-chip v-if="getSharedConfigName(item.config)" small color="purple" text-color="white">
              {{ getSharedConfigName(item.config) }}
            </v-chip>
            <span v-else class="grey--text">—</span>
          </template>

          <template v-slot:item.config.meta.uid="{ item }">
            <v-card-text class="blue--text subtitle-2 ma-0 pa-0">{{ getOwner(item.config) }}</v-card-text>
          </template>

          <template v-slot:item.config.meta.updated="{ item }">
            {{ getUpdated(item.config) | formatDate }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom max-width="50%">
              <template v-slot:activator="{ on, attrs }">
                <v-btn :loading="loading == item.config.uid"
                       icon
                       color="orange"
                       v-bind="attrs"
                       v-on="on"
                       class="ma-0 pa-0"
                       @click="onPressInfo(item.config)">
                  <v-icon size="22">mdi-information</v-icon>
                </v-btn>
              </template>
              <span>{{ item.config.info?.length > 0 ? item.config.info : "no info" }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :disabled="!ifCanCopyConfigToMyConfigs()"
                       icon
                       color="teal lighten-1"
                       v-bind="attrs"
                       v-on="on"
                       class="ma-0 pa-0"
                       @click="onPressCopy(item.config)">
                  <v-icon size="22">mdi-content-copy</v-icon>
                </v-btn>
              </template>
              <span>Copy to My Configs</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :loading="loading == item.config.uid"
                       elevation="0"
                       small
                       color="blue"
                       v-bind="attrs"
                       v-on="on"
                       class="white--text ma-0 pa-0"
                       :disabled="true || item.config.deployed == true || !ifCanDeployConfigToStorage()"
                       icon
                       @click="onPressDeploy(item.config)">
                  <v-icon size="22" class="mr-0">mdi-database-arrow-right-outline</v-icon>
                </v-btn>
              </template>
              <span>Deploy to storage</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :loading="loading == item.config.uid"
                       elevation="0"
                       small
                       color="blue"
                       v-bind="attrs"
                       v-on="on"
                       class="white--text  ma-0 pa-0"
                       :disabled="true || item.config.deployed == true || !ifCanDeployConfigToStorage()"
                       icon
                       @click="onPressDeployWithMerge(item.config)">
                  <v-icon size="22" class="mr-0">mdi-database-refresh-outline</v-icon>
                </v-btn>
              </template>
              <span>Deploy to storage with replace</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :loading="loading == item.config.uid"
                       icon
                       color="red"
                       v-bind="attrs"
                       v-on="on"
                       class="ma-0 pa-0"
                       :disabled="!ifCanEditConfigTag()"
                       @click="onPressDelete(item.config)">
                  <v-icon size="22">mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete config</span>
            </v-tooltip>
          </template>
        </v-data-table>
    </v-card>
</template>

<script>

import {ConfigStoreUtils} from "@/modules/mconfigs/database/ConfigStoreUtils";
import {ConfigBackendUtils} from "@/modules/mconfigs/database/ConfigBackendUtils";
import {ConfigModel} from "@/modules/mconfigs/models/ConfigModel";
import {PermissionsStoreUtils} from "@/modules/mpermissions/database/PermissionsStoreUtils";
import {SlackLogManager} from "@/common/SlackLogManager";
import BranchStoreUtils from "@/modules/mconfigs/database/BranchStoreUtils";
import BranchConfigsModel from "@/modules/mconfigs/models/BranchConfigsModel";
import {BranchModel} from "@/modules/mconfigs/models/BranchModel";
import CPreReleaseManagement from "@/modules/mconfigs/views/pre-release/c-pre-release-management.vue";
import {SharedConfigMerge} from "@/modules/mconfigs/utils/SharedConfigMerge";

export default {
    name: "c-pre-release-table",
    components: {CPreReleaseManagement},
    data: () => ({
        headers: [
            {text: 'Branch', sortable: true, value: 'branch.name', class: "white blue--text", color: "red"},
            {text: 'Group', sortable: true, value: 'config.group', class: "white blue--text", color: "red"},
            {text: 'Tag', sortable: true, value: 'config.tag', class: "white blue--text", color: "red"},
            {text: 'Id', sortable: true, value: 'config.uid', class: "white blue--text"},
            {text: 'Map Id', sortable: true, value: 'config.mapId', class: "white blue--text", color: "red"},
            {text: 'Shared Config', sortable: true, value: 'config.sharedConfig', class: "white blue--text"},
            {text: 'Owner', sortable: true, value: 'config.meta.uid', class: "white blue--text"},
            {text: 'Updated', sortable: true, value: 'config.meta.updated', class: "white blue--text"},
            {text: 'Actions', sortable: false, value: 'actions', width: '200px', class: "white blue--text"}
        ],
        loading: null,
        sortBy: 'branch.order',
        sortDesc: false,
        managementPanelExpanded: undefined,
    }),
    methods: {
        getMyUserUid(){
            return PermissionsStoreUtils.getMyUserUid();
        },
        ifCanEditConfigTag(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_PRE_EDIT_TAG.id)
        },
        ifCanCopyConfigToMyConfigs(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_PRE_COPY_TO_MY.id)
        },
        ifCanDeployConfigToStorage(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_PRE_DEPLOY_TO_STORAGE.id)
        },
        ifCanSetInfo(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_PRE_SET_INFO.id)
        },
        getUserName(user){
            return PermissionsStoreUtils.getUserName(user.uid)
        },
        getSharedConfigName(config) {
            if (!config.sharedConfig) return null;
            let shared = ConfigStoreUtils.getSharedConfigs();
            let found = shared.find(s => s.uid === config.sharedConfig);
            return found ? found.group : config.sharedConfig;
        },
        onPressInfo(config){
            if (!this.ifCanSetInfo()){
                this.$notify({title: 'No permissions', text: "", type: "error"});
                return
            }
            this.$emit('show-configs-popup-edit-info', config)
        },
        getTagText(tag){
            let text = tag || ""
            if (text.length > 25){
                text = text.substring(0,30) + "..."
            }
            return text// + text.length
        },
        onPressEditTag(config) {
            this.$emit('show-c-edit-tag-popup', config)
        },
        onPressEditGroup(config) {
          this.$emit('show-c-edit-group-popup', config)
        },
        onPressDeployWithMerge(config) {
            this.$emit('show-c-p-r-deploy-with-merge-popup', config)
        },
        onPressDelete(config) {
            this.$confirm("This action is not revertable. Are you sure you want to delete this config?").then(() => {
                this.loading = config.uid;
                ConfigStoreUtils.removeReleaseConfig(config.uid)
                    .then(() => {
                        this.$notify({title: 'Config successfully deleted!', text: "", type: "success"});
                    })
                    .catch((error) => {
                        this.$notify({title: 'Config delete failed!', text: error, type: "error"});
                    })
                    .finally(() => {
                        this.loading = null;
                    });
            });
        },
        onPressDeploy(config) {
            if (config.info == ""){
                this.$notify({title: 'Add comment before deploy!', type: "error"});
                return;
            }
            this.$confirm("Are you sure?").then(() => {
                this.loading = config.uid
                ConfigStoreUtils.getConfigData("configs_data/" + config.uid)
                    .then((mapData) => {
                        const deployContent = (content, sharedConfigSnapshot) => {
                            ConfigBackendUtils.deployConfig({group:config.group, owner:config.meta.uid, tag:config.tag, content: content, info:config.info}, config.uid)
                            .then((fileName) => {
                                this.loading = null
                                this.$notify({title: 'Config successfully deployed!', text: "", type: "success"});

                                ConfigStoreUtils.saveDeployedSource(
                                    mapData, config.sharedConfig || null, sharedConfigSnapshot || null, fileName[0]
                                ).catch((error) => {
                                    // Log and notify on failure to persist the deployed source snapshot
                                    // without affecting the deploy success flow.
                                    // eslint-disable-next-line no-console
                                    console.error('Failed to save deployed source snapshot:', error);
                                    this.$notify({
                                        title: 'Failed to save deployed source snapshot!',
                                        text: error,
                                        type: 'error',
                                    });
                                });

                                let deployData = {
                                    'icon_emoji': ':hammer:',
                                    'text': "<@"+PermissionsStoreUtils.getMyName()+"> deploy config to `RELEASE`",
                                    'attachments': [{
                                        'color': '#40c5ee',
                                        'fields': [
                                            {"title": "Environment", 'value': SlackLogManager.getEnv(), 'short': true},
                                            {"title": "Name", 'value': config.tag, 'short': true},
                                            {"title": "Target Config Id", 'value': config.uid, 'short': true},
                                            {"title": "File Name", 'value': fileName[0], 'short': true},
                                            {"title": "Mode", 'value': config.sharedConfig ? "shared-merge" : "direct", 'short': true},
                                            {"title": "Group", 'value': config.group, 'short': true},
                                            {"title": "Comment", 'value': config.info, 'short': true},
                                        ]
                                    }]
                                }
                                SlackLogManager.log(deployData)
                            })
                            .catch((error) => {
                                this.loading = null
                                this.$notify({title: 'Config deploy failed!', text: error, type: "error"});
                            })
                        };

                        if (config.sharedConfig) {
                            ConfigStoreUtils.getConfigData("configs_data/" + config.sharedConfig)
                                .then((sharedData) => {
                                    const merged = SharedConfigMerge.mergeSharedIntoMap(sharedData, mapData);
                                    deployContent(merged, sharedData);
                                })
                                .catch((error) => {
                                    this.loading = null;
                                    this.$notify({title: 'Failed to load shared config data!', text: error, type: "error"});
                                });
                        } else {
                            deployContent(mapData);
                        }
                    }).catch((error) => {
                    this.loading = null;
                    this.$notify({title: 'Failed to get config data to copy!', text: error, type: "error"});
                })
            })
        },
        onPressCopy(config) {
            this.loading = config.uid;
            let configCopy = new ConfigModel(config.group + "_release_copy", config.mapId)
            configCopy.info = config.info;
            configCopy.meta = config.meta;
            configCopy.tag = config.tag;

            ConfigStoreUtils.getConfigData("configs_data/" + config.uid)
            .then((data) => {
                ConfigStoreUtils.pushConfig(configCopy, data).then(() => {
                    this.$notify({title: 'Config successfully copied!', text: "", type: "success"});
                }).catch((error) => {
                    this.$notify({title: 'Config copied failed!', text: error, type: "error"});
                }).finally(() => {
                    this.loading = null;
                })
            }).catch((error) => {
                this.$notify({title: 'Failed to get config data to copy!', text: error, type: "error"});
                this.loading = null;
            })
        },
        getOwner(config) {
            if (config.meta == null) {
                return "no metadata"
            }
            return this.getUserName(config.meta)
        },
        getUpdated(config) {
            if (config.meta == null) {
                return "no metadata"
            }
            return config.meta.updated
        },
    },
    computed: {
        configs() {
            let configs = ConfigStoreUtils.getReleaseConfigs();
            let branches = BranchStoreUtils.getValues();
            let emptyBranch = new BranchModel();
            emptyBranch.name = "No Branch";
            emptyBranch.order = 999;
            return configs.map((config) => {
                let branch = branches.find(b => b.groups.find(g => g.name?.toLowerCase() === config.group?.toLowerCase()) !== undefined);
                return new BranchConfigsModel(branch ? branch : emptyBranch, config);
            });
        },
    }
}
</script>

<style scoped>
</style>